/**
 * The Midwinter Physiotherapy mark.
 *
 * Geometry lives here so the web dashboard (plain SVG) and the patient app
 * (react-native-svg) draw exactly the same shape from one definition. The
 * canonical file as supplied is `assets/logo.svg`; this is that file's
 * geometry, nothing more.
 *
 * ON THE COLOURS: `brand` is the mark exactly as supplied, and on the light
 * surfaces both apps now use it measures 9.9:1 — so it is the default, and the
 * apps draw the real thing rather than an approximation of it.
 *
 * `onDark` stays for dark grounds. There the supplied body colour is about
 * 1.9:1, under the 3:1 a graphic needs, and the mark collapses into a floating
 * dot; that variant keeps the hue (272deg) and saturation and raises lightness
 * until it passes at ~4.7:1. Use it on a dark splash, a dark email template or
 * anything else not on paper-white.
 */

export const LOGO_VIEWBOX = '0 0 512 512';

/** The upper ellipse. */
export const LOGO_ELLIPSE = {
  cx: 251.44821,
  cy: 103.13466,
  rx: 44.927155,
  ry: 53.403973,
} as const;

/** The lower body. */
export const LOGO_BODY_PATH =
  'm 360.81038,446.96281 c -44.75979,-38.3683 -61.31002,-105.12626 -68.53608,-116.22377 -7.2365,-11.11354 -78.66624,-8.12463 -83.51164,-0.45383 -6.40484,10.13958 -18.73264,71.90407 -63.91264,115.3375 -15.72732,15.11934 -50.419721,18.76935 -69.204501,-3.30759 -15.270402,-17.94664 -14.642224,-54.15299 6.841313,-71.70582 39.915178,-32.61215 95.271368,-44.28395 112.273258,-55.07394 9.05999,-5.74978 7.57167,-78.10405 0.18534,-82.89526 -18.46062,-11.97466 -78.64225,-25.90133 -109.589991,-52.36637 -26.45951,-22.62692 -28.930381,-57.91795 -5.891576,-80.290109 29.542457,-28.687613 69.809757,-8.33442 81.504477,8.138579 10.28686,14.48991 27.10899,53.48066 48.11555,107.16548 4.68795,11.98063 79.25322,13.47407 84.75193,0.65104 23.43271,-54.64521 27.32201,-75.96665 52.35882,-111.9322 16.70161,-23.991987 59.44622,-22.430271 78.65512,-4.12295 19.94186,19.00587 20.09178,57.29145 2.28971,75.30433 -25.00425,25.30035 -100.23075,50.21271 -121.03377,60.14115 -8.1782,3.90313 -5.42368,75.46388 1.30033,79.32097 18.64479,10.69513 97.57248,32.47942 118.88268,61.36191 18.54149,25.12996 17.45897,49.21798 -0.29841,68.23826 -15.03693,16.10635 -47.29108,18.04701 -65.17992,2.71262 z';

export type LogoTone = 'onDark' | 'brand';

export const LOGO_COLOURS: Record<LogoTone, { ellipse: string; body: string }> = {
  /** Exactly as supplied. The default: both apps are light. */
  brand: { ellipse: '#CE8DFD', body: '#5B2A86' },
  /** For dark grounds only. Same hue, enough lightness to be seen. */
  onDark: { ellipse: '#CE8DFD', body: '#9C64CE' },
};

export const PRACTICE_NAME = 'Midwinter Physiotherapy';
export const PRACTICE_URL = 'https://midwinterphysiotherapy.co.uk';
